<template>
  <b-container fluid class="contents">
    <b-row class="mb-3">
      <b-col class="d-flex align-items-center">
        <span class="contents-title">Our brands</span>
        <i class="guide-tooltip" id="contents-tooltip"></i>
        <b-popover
          target="contents-tooltip"
          triggers="hover"
          content="Introduce your brands to your customers. Show up to 7 featured brands on your homepage. You may choose to disable this if you are a single brand business."
        ></b-popover>
        <switches v-model="form.our_brands_enable"
          theme="custom"
          color="primary"
          type-bold="true"
          style="top: 13px; left: 10px;"
          @input="handleChanges(null)"
          :emit-on-mount="false">
        </switches>
      </b-col>
    </b-row>
    <div v-if="isLoadingAdvancedSettingsOurBrands" class="w-100 min-height-300px d-flex align-items-center justify-content-center">
      Loading...
    </div>
    <template v-else>
      <b-row v-for="(brand, index) in form.our_brands" :key="`brand_${index}`" class="border-bottom mb-3">
        <b-col md="12">
          <b-form-group label="Add Brand" label-class="d-flex justify-content-between align-items-center">
            <template slot="label">
              Add Brand
              <b-button variant="ghost" @click="removeBrand(index)" v-if="form.our_brands.length > 1">
                <img src="@/assets/images/u_trash-alt.svg" alt="Delete" class="img-fluid">
              </b-button>
            </template>
            <b-row>
              <b-col md="4">
                <b-form-select
                  v-model="brand.uuid" :class="{ 'text-muted': isLoadingBrands || !brand.uuid }"
                  @change="handleChanges({ formKey: 'our_brands', index: index, uuid: brand.uuid })">
                  <option value="null" disabled>{{ isLoadingBrands ? 'Loading brands...' : 'Select' }}</option>
                  <option v-for="(brandOption, brandOptionIndex) in brandsOptions"
                    :key="`brandOption_${brandOptionIndex}`" :value="brandOption.value"
                    :disabled="selectedBrands.includes(brandOption.value)">
                    {{ brandOption.text }}
                  </option>
                </b-form-select>
                <b-form-invalid-feedback :force-show="!isInvalid(`our_brands.${index}.name`)">{{ errorMessage(`our_brands.${index}.name`)  }}</b-form-invalid-feedback>
              </b-col>
            </b-row>
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-row>
            <b-col md="4">
              <b-form-group label="Upload Brand Logo">
                <div class="position-relative mt-3">
                  <img
                    v-if="brand.logo"
                    src="@/assets/images/icons/remove.svg"
                    alt="remove-brand-logo-icon"
                    class="remove-image cursor-pointer bg-white rounded-circle"
                    @click="clearBrandImage(index)"
                  />
                  <div
                    :class="['image-placeholder border rounded position-relative cursor-pointer', { 'img-empty': !brand.logo }]" 
                    @click="handleCroppa(index)"
                  >
                    <div v-if="brand.logo" class="w-100 h-100">
                      <b-img :src="brand.logo" fluid  class="brand-img w-100 h-100" />
                    </div>
                    <span class="camera-icon-container bg-white mr-1 mb-1 position-absolute p-2 rounded-circle d-flex align-items-center shadow">
                      <img src="@/assets/images/camera.svg" alt="camera-brand-logo-icon" />
                    </span>
                  </div>
                  <em class="image-size">Required Size - 335 x 140 px</em>
                </div>
                <b-form-invalid-feedback :force-show="!isInvalid(`our_brands.${index}.logo`)">{{ errorMessage(`our_brands.${index}.logo`)  }}</b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button
            :variant="form.our_brands.length >= 7 ? 'disabled' : 'ghost'"
            class="text-primary p-0 shadow-none"
            @click="addBrand"
            :disabled="form.our_brands.length >= 7">
            <img 
              class="img-fluid"
              src="@/assets/images/icons/add.svg"
              alt="Add Icon"
              height="26"
              width="26"
            > Add New Brand
          </b-button>
        </b-col>
      </b-row>
    </template>
    <UploadBrandLogoModal @setBrandLogoImage="setBrandLogoImage" />
  </b-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import Switches from "vue-switches";
import UploadBrandLogoModal from "@/views/design/modals/UploadBrandLogoModal";
import notification from "@/services/notification";

export default {
  name: "designAdvancedSettingsOurBrands",
  components: { Switches, UploadBrandLogoModal },
  data() {
    return {
      storedForm: {},
      form: {
        enabled: true,
        our_brands: [
          {
            uuid: null,
            name: null,
            logo: null
          },
          {
            uuid: null,
            name: null,
            logo: null
          }
        ]
      },
      brandIndex: null,
    }
  },
  computed: {
    ...mapGetters({
      brands: "brands/visibleBrands",
      ourBrands: "designs/ourBrands",
      isLoadingBrands: "brands/loading",
      isLoadingAdvancedSettingsOurBrands: "designs/loading",
      ourBrandErrors: "designs/errors"
    }),
    selectedBrands: {
      get() {
        return this.form.our_brands.map(brand => brand.uuid)
      },
      set(brands) {
        return brands;
      }
    },
    brandsOptions: {
      get() {
        return this.brands ?
          this.brands.map(brand => {
            return {
              value: brand.uuid,
              text: brand.name
            }
          })
          : []
      },
      set(brands) {
        return brands;
      }
    }
  },
  mounted() {
    this.$root.$on('handleSave', (routeName) => {
      if (routeName === 'designAdvancedSettingsOurBrands') this.saveOurBrands();
    });
    this.$root.$on('saveChangesCancelled', (value) => {
      if (value) this.form = JSON.parse(JSON.stringify(this.ourBrands));
      this.setErrors(null);
    });
  },
  beforeDestroy() {
    this.$root.$off('handleSave');
    this.$root.$off('saveChangesCancelled');
  },
  async created() {
    this.getVisibleBrands();
    await this.getAdvancedSettingsOurBrands();
    this.form = JSON.parse(JSON.stringify(this.ourBrands));
  },
  methods: {
    ...mapActions({
      getVisibleBrands: "brands/getVisibleBrands",
      getAdvancedSettingsOurBrands: "designs/getAdvancedSettingsOurBrands",
      saveAdvancedSettingsOurBrands: "designs/saveAdvancedSettingsOurBrands",
    }),
    ...mapMutations({
      setErrors: "designs/errors"
    }),
    addBrand() {
      this.form.our_brands.push({ uuid: null, name: null, logo: null });
      this.$root.$emit('showSaveAlert', true);
    },
    async saveOurBrands() {
      this.setErrors(null);

      const errors = {};

      this.form.our_brands.map((brand, index) => {
        if (!brand.uuid) {
          errors[`our_brands.${index}.uuid`] = "Uuid is required.";
          errors[`our_brands.${index}.name`] = "Name is required.";
        }
        if (!brand.logo) errors[`our_brands.${index}.logo`] = "Logo is required.";

        if (!brand.uuid || !brand.logo) notification.warning("Oops, something went wrong.");
      });

      this.setErrors(errors);

      if (Object.keys(errors).length === 0) {
        await this.saveAdvancedSettingsOurBrands(this.form);
        if (this.ourBrandErrors && Object.keys(this.ourBrandErrors).length === 0) this.$root.$emit('showSaveAlert', false);
      }
    },
    removeBrand(index) {
      this.form.our_brands.splice(index, 1);
      this.$root.$emit('showSaveAlert', true);
    },
    clearBrandImage(index) {
      this.form.our_brands[index].logo = null;
      this.$root.$emit('showSaveAlert', true);
    },
    handleCroppa(index) {
      this.brandIndex = index; // assign active brandIndex for setting croppa image value
      this.$root.$emit('bv::show::modal', 'upload-brand-logo-modal')
    },
    setBrandLogoImage(image) {
      this.form.our_brands[this.brandIndex].logo = image;
      this.$root.$emit('showSaveAlert', true);

      this.$nextTick(() => {
        this.brandIndex = null; // reset active brandIndex after assigning image base64
      });
    },
    handleChanges(params) {
      if (params) this.form[params.formKey][params.index]['name'] = this.brandsOptions.find(brand => brand.value == params.uuid).text;
      this.$root.$emit('showSaveAlert', true);
    },
    isInvalid(field) {
      return this.ourBrandErrors && Object.keys(this.ourBrandErrors).includes(field) ? false : null;
    },
    errorMessage(field) {
      return this.ourBrandErrors && field in this.ourBrandErrors
        ? this.ourBrandErrors[field]
        : null;
    }
  },
};
</script>

<style lang="scss" scoped>
.remove-image {
  position: absolute;
  left: 325px !important;
  top: -10px !important;
  z-index: 9;
}
.image-placeholder {
  width: 335px !important;
  height: 140px !important;
  &.img-empty {
    background: #FAFAFA url("../../../assets/images/default-image.svg") no-repeat center;
  }
  .brand-img {
    object-fit: cover;
  }
  .camera-icon-container {
    bottom: 5px;
    right: 5px;
  }
}
em.image-size {
  font-size: 11px;
  color: #87929d;
}
.min-height-300px {
  min-height: 300px;
}
</style>